import React, { useState, useRef, useEffect } from 'react';
import { db } from '../../firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { FiMoreVertical } from 'react-icons/fi';
import SlideToAccept from '../buttons/SlideToAccept';

// Predefined list of categories
const PREDEFINED_CATEGORIES = [
  'Assault/Battery',
  'Recklessness',
  'Theft',
  'Property Damage',
  'Vehicle',
  'Other',
];

const AddCaseTab = ({ user, onTabSwitch }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSlideToAccept, setShowSlideToAccept] = useState(false);
  const categoryMenuRef = useRef(null);

  // Close category dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredCategories = PREDEFINED_CATEGORIES.filter(category =>
    category.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedCategories.includes(category)
  );

  const handleCategorySelect = (category) => {
    if (!selectedCategories.includes(category)) {
      setSelectedCategories([...selectedCategories, category]);
    }
    setSearchTerm('');
    setIsCategoryOpen(false);
  };

  const handleRemoveCategory = (categoryToRemove) => {
    setSelectedCategories(selectedCategories.filter(cat => cat !== categoryToRemove));
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSelectedCategories([]);
    setMessage('');
    setShowSlideToAccept(false);
  };

  const handleSlideToAcceptConfirm = () => {
    // Hide the slide to accept and reset the form
    setShowSlideToAccept(false);
    setMessage('✅ Casefile created successfully!');
    
    // Reset the form after a brief delay
    setTimeout(() => {
      resetForm();
    }, 2000);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setMessage('Authentication required. Please sign in.');
      return;
    }
    
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    
    if (trimmedTitle.length < 3) {
      setMessage('Case title must be at least 3 characters long.');
      return;
    }
    
    if (trimmedTitle.length > 100) {
      setMessage('Case title cannot exceed 100 characters.');
      return;
    }
    
    if (trimmedDescription.length < 10) {
      setMessage('Case description must be at least 10 characters long.');
      return;
    }
    
    if (trimmedDescription.length > 2000) {
      setMessage('Case description cannot exceed 2000 characters.');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const docData = {
        title: trimmedTitle,
        description: trimmedDescription,
        categories: selectedCategories,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        creatorEmail: user.email,
        isPublic: false, // Default to private
        
        // Privacy controls for marketplace
        showNameToViewers: false,  // V1: Hide casefile name from buyers by default
        privacyLocked: false       // Allows privacy changes until first sale
      };

      await addDoc(collection(db, 'casefiles'), docData);
      
      // Show the slide to accept component
      setShowSlideToAccept(true);
      
    } catch (error) {
      console.error('Error creating casefile:', error);
      setMessage('❌ Failed to create casefile. Please try again.');
    }

    setIsLoading(false);
  };

  return (
    <div className="tab-content add-case-tab">
      <div className="tab-header">
        <h2>Create New Casefile</h2>
      </div>

      <div className="case-form-container">
        <form onSubmit={handleSubmit} className="case-form">
          <div className="form-group">
            <label htmlFor="case-title">Case Title *</label>
            <input
              type="text"
              id="case-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter case title (3-100 characters)"
              maxLength="100"
              required
              disabled={isLoading}
            />
            <div className="char-counter">
              {title.length}/100 characters
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="case-description">Case Description *</label>
            <textarea
              id="case-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the case details (10-2000 characters)"
              rows="6"
              maxLength="2000"
              required
              disabled={isLoading}
            />
            <div className="char-counter">
              {description.length}/2000 characters
            </div>
          </div>

          <div className="form-group">
            <label>Categories (Optional)</label>
            <div className="categories-section">
              {/* Selected Categories Display */}
              {selectedCategories.length > 0 && (
                <div className="selected-categories">
                  {selectedCategories.map((category) => (
                    <span key={category} className="category-tag">
                      {category}
                      <button
                        type="button"
                        onClick={() => handleRemoveCategory(category)}
                        className="remove-category-btn"
                        aria-label={`Remove ${category}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Category Selection Dropdown */}
              <div className="category-selector" ref={categoryMenuRef}>
                <div className="category-input-container">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setIsCategoryOpen(true)}
                    placeholder="Search and select categories..."
                    className="category-search-input"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                    className="category-dropdown-btn"
                    disabled={isLoading}
                  >
                    <FiMoreVertical />
                  </button>
                </div>

                {isCategoryOpen && filteredCategories.length > 0 && (
                  <div className="category-dropdown">
                    {filteredCategories.map((category) => (
                      <button
                        key={category}
                        type="button"
                        onClick={() => handleCategorySelect(category)}
                        className="category-option"
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {message && (
            <div className={`form-message ${message.includes('✅') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              disabled={isLoading || !title.trim() || !description.trim()}
              className="submit-btn"
            >
              {isLoading ? 'Creating Case...' : 'Create Casefile'}
            </button>
            
            <button
              type="button"
              onClick={resetForm}
              disabled={isLoading}
              className="reset-btn"
            >
              Reset Form
            </button>
          </div>
        </form>
      </div>

      {/* Slide to Accept Component */}
      <SlideToAccept
        isVisible={showSlideToAccept}
        onAccept={handleSlideToAcceptConfirm}
        onCancel={() => {
          // Reset form and hide the slide to accept
          resetForm();
          setMessage('Case creation cancelled.');
          setTimeout(() => setMessage(''), 3000);
        }}
      />

    </div>
  );
};

export default AddCaseTab; 